import { z } from 'zod';
import { countriesList } from '@archipro-website/localisation';

/**
 * Add the https protocol to a URL if it does have a protocol already. Relative
 * URLs are returned as is.
 * @param href The URL to ensure has a protocol.
 * @returns URL with protocol if needed.
 */
export function ensureProtocol(href: string): string {
    const lower = href.toLowerCase();
    if (
        lower.startsWith('http://') ||
        lower.startsWith('https://') ||
        href.startsWith('/')
    ) {
        return href;
    }
    return `https://${href}`;
}
/**
 * Convert a string to a URL object if possible. Add the https protocol if no
 * protocol is present. Return `null` if the URL is invalid.
 * @param url The URL string to convert.
 * @param base The base URL to use if the URL is relative.
 * @returns The URL object if valid, `null` otherwise.
 */
export function toAbsoluteURL(url: string, base?: string): URL | null {
    try {
        return new URL(url, base);
    } catch {
        // Maybe no protocol is present.
    }
    const withProtocol = ensureProtocol(url);
    try {
        return new URL(withProtocol, base);
    } catch {
        return null;
    }
}

/**
 * Check if a string is a valid absolute or relative URL.
 * @param href The URL to check.
 * @returns The string is a valid URL.
 */
export function isValidURL(href: string): boolean {
    if (href.startsWith('/')) {
        const baseUrl =
            typeof window !== 'undefined'
                ? window.location.origin
                : 'https://archipro.co.nz';
        try {
            const url = new URL(href, baseUrl);
            return z.string().url().safeParse(url.toString()).success;
        } catch {
            return false;
        }
    }
    return z.string().url().safeParse(href).success;
}

/**
 * Check if a hostname is a localhost. Can also handle URLs with https/http protocols.
 * @param hostname The hostname to check.
 * @returns The hostname is a localhost.
 */
export function isLocalhost(hostname: string): boolean {
    if (hostname.startsWith('https://') || hostname.startsWith('http://')) {
        try {
            hostname = new URL(hostname).hostname;
        } catch {
            return false;
        }
    }
    return ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(
        hostname.toLowerCase(),
    );
}

/**
 * Normalise scheme and host to lowercase while preserving the case of path,
 * query and fragment. Prepends `https://` when no protocol is present.
 * Falls back to the `ensureProtocol`-only result when the URL cannot be parsed.
 * @param href Raw URL string from user input.
 * @returns Normalised URL string.
 */
export function normaliseURL(href: string): string {
    const withProtocol = ensureProtocol(href.trim());
    try {
        return new URL(withProtocol).href;
    } catch {
        return withProtocol;
    }
}

/**
 * Check if a string is a valid IP address.
 * @param hostname The hostname to check.
 * @returns The hostname is a valid IP address.
 */
export function isIPAddress(hostname: string): boolean {
    return z.string().ip().safeParse(hostname).success;
}

/**
 * True when a hostname has at least two non-empty labels (e.g. `example.com`)
 * and is not an IP address. Strips trailing dots so `example.com.` doesn't
 * sneak through.
 */
export function hasValidTLD(hostname: string): boolean {
    if (isIPAddress(hostname)) return false;
    const labels = hostname.replace(/\.+$/, '').split('.');
    return labels.length >= 2 && labels.every((l) => l.length > 0);
}

/**
 * Check if a string is a valid URL and the same domain as the current page. This
 * can be used to check if a URL...
 * @param href The URL to check.
 * @returns The string is a valid URL and the same domain as the current page.
 */
export function isSameDomainURL(href: string): boolean {
    const fullUrl = ensureProtocol(href);
    if (!isValidURL(fullUrl)) {
        return false;
    }
    if (href.startsWith('/')) {
        return true;
    }
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        const url = new URL(fullUrl);
        return url.origin === window.location.origin;
    } catch {
        return false;
    }
}

/**
 * Check if a string is a valid ArchiPro URL. This includes relative URLs and
 * ArchiPro URLs with different domains, e.g. dev.archipro.co.nz.
 * @param href The URL to check.
 * @returns The string is an ArchiPro URL.
 */
export function isArchiProURL(href: string): boolean {
    const fullUrl = ensureProtocol(href);
    if (!isValidURL(fullUrl)) {
        return false;
    }
    // Relative URLs are valid ArchiPro URLs.
    if (href.startsWith('/')) {
        return true;
    }
    let url: URL;
    try {
        url = new URL(fullUrl);
    } catch {
        return false;
    }
    const hostname = url.hostname.toLowerCase();
    const archiProDomains = Object.values(countriesList).map(({ domain }) => {
        return domain.toLowerCase();
    });
    const isProduction = import.meta.env.PROD;
    return (
        archiProDomains.some((domain) => {
            return hostname === domain || hostname.endsWith(`.${domain}`);
        }) ||
        (!isProduction && isLocalhost(hostname))
    );
}

/**
 * Check if a string is a valid URL and an external URL.
 * @param href The URL to check.
 * @returns The string is a valid URL and an external URL.
 */
export function isExternalURL(href: string): boolean {
    const fullUrl = ensureProtocol(href);
    if (
        !isValidURL(fullUrl) ||
        isSameDomainURL(fullUrl) ||
        isArchiProURL(fullUrl)
    ) {
        return false;
    }
    try {
        const url = new URL(fullUrl);
        if (isLocalhost(url.hostname)) {
            return false;
        }
    } catch {
        return false;
    }
    return true;
}
